import { useState, useEffect } from "react";
import { RichText } from "@wordpress/block-editor";

export const FundraisingBlock = ({
  title,
  appealText,
  amountLabel,
  frequencyLabel,
  defaultFrequency,
  amountConfiguration,
  validateOtherAmount,
  buttonText,
  footerText,
  setAttributes = null,
  isSelected = false,
  contact_id = null,
  mode = "frontend",
  checksum = null,
}) => {
  const [amount, setAmount] = useState("");
  const [otherAmount, setOtherAmount] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [frequency, setFrequency] = useState(defaultFrequency);
  const [amountValues, setAmountValues] = useState(
    amountConfiguration[defaultFrequency].amounts
  );

  useEffect(() => {
    const allFees = amountValues.split(",");
    setMinAmount(allFees[0]);
    setAmount(allFees[0]);
  }, [amountValues]);

  const handleAmount = (value) => {
    setOtherAmount("");
    setAmount(value);
    setErrorMessage("");
  };

  const handleOtherAmount = (value) => {
    setAmount("");
    setOtherAmount(value);
    setErrorMessage("");
  };

  const handleFrequencyChange = (value) => {
    // here we need to set the correct amounts
    setAmount("");
    setOtherAmount("");
    setErrorMessage("");
    setFrequency(value);
    setAmountValues(amountConfiguration[value].amounts);
  };

  const handleButtonClick = () => {
    if (!amount && !otherAmount) {
      setErrorMessage("Please select an amount.");
      return;
    }

    let selectedAmount = amount;
    if (otherAmount) {
      if (validateOtherAmount && parseFloat(otherAmount) < parseFloat(minAmount)) {
        setErrorMessage(`Please contribute at least £${minAmount}.`);
        return;
      }

      selectedAmount = otherAmount;
    }

    // convert string to int
    selectedAmount = parseFloat(selectedAmount);

    let urlParams = `amount=${selectedAmount}`;

    if (frequency) {
      urlParams = `${urlParams}&rf=${frequency}`;
    }

    if (contact_id && checksum) {
      urlParams = `${urlParams}&cid=${contact_id}&cs=${checksum}`;
    }

    window.location.href = `/civicrm/contribute/transact/?reset=1&id=${amountConfiguration[frequency].contributionPage}&${urlParams}`;
  };

  // unigue identifier to distinguish when same widget in added multiple times
  const uniqueIdentifier = Date.now();

  // frequency section display
  let frequencies = [];
  for (const freq in amountConfiguration) {
    if (amountConfiguration[freq].amounts) {
      if (amountConfiguration[freq].label) {
        frequencies.push(
          <Fragment key={`freq-${amountConfiguration[freq].label}-${uniqueIdentifier}`} >
            <input
              id={`freq-${amountConfiguration[freq].label}-${uniqueIdentifier}`}
              type="radio"
              value={freq}
              name="frequency"
              checked={frequency === freq}
              onChange={(e) => handleFrequencyChange(e.target.value)}
            />
            <label
              htmlFor={`freq-${amountConfiguration[freq].label}-${uniqueIdentifier}`}
              className="frequency"
            >
              {amountConfiguration[freq].label}
            </label>
          </Fragment>
        );
      }
    }
  }

  const frequencySection = (
    <>
      {frequencyLabel || isSelected ? (
        <div className="frequency-label">
          {mode === "preview" ? (
            <RichText
              tagName="div"
              value={frequencyLabel}
              allowedFormats={[]}
              onChange={(frequencyLabel) => setAttributes({ frequencyLabel })}
              onReplace={() => {}}
              onSplit={() => {}}
              placeholder={"Frequency label"}
            />
          ) : (
            frequencyLabel
          )}
        </div>
      ) : (
        ""
      )}
      {frequencies.length > 0 ? (
        <div className="frequency-group">{frequencies}</div>
      ) : (
        ""
      )}
    </>
  );

  // amount section display
  let amountFees = "";
  amountFees = amountValues.split(",").map((fee) => {
    return (
      <Fragment key={`amount-${fee}-${uniqueIdentifier}`}>
        <input
          id={`amount-${fee}-${uniqueIdentifier}`}
          type="radio"
          value={fee}
          name="amount"
          checked={amount === fee}
          onChange={(e) => handleAmount(e.target.value)}
        />
        <label htmlFor={`amount-${fee}-${uniqueIdentifier}`} className="fee-label">
          &pound;{fee}
        </label>
      </Fragment>
    );
  });

  amountFees = (
    <div className="fee-group">
      {amountFees}
      <input
        type="number"
        name="otherAmount"
        placeholder="Other"
        onChange={(e) => handleOtherAmount(e.target.value)}
        value={otherAmount}
      />
    </div>
  );

  const amountSection = (
    <>
      {amountLabel || isSelected ? (
        <div className="amount-label">
          {mode === "preview" ? (
            <RichText
              tagName="div"
              value={amountLabel}
              allowedFormats={[]}
              onChange={(amountLabel) => setAttributes({ amountLabel })}
              onReplace={() => {}}
              onSplit={() => {}}
              placeholder={"Amount label"}
            />
          ) : (
            amountLabel
          )}
        </div>
      ) : (
        ""
      )}

      {amountFees}
    </>
  );

  return (
    <>
      <h3>
        {mode === "preview" ? (
          <RichText
            tagName="div"
            value={title}
            allowedFormats={[]}
            onChange={(title) => setAttributes({ title })}
            onReplace={() => {}}
            onSplit={() => {}}
            placeholder={"Add a custom title"}
          />
        ) : (
          title
        )}
      </h3>

      {appealText || isSelected ? (
        <>
          {mode === "preview" ? (
            <RichText
              tagName="p"
              className="appeal-text"
              value={appealText}
              allowedFormats={[]}
              onChange={(appealText) => setAttributes({ appealText })}
              onReplace={() => {}}
              onSplit={() => {}}
              placeholder={"Appeal text"}
            />
          ) : (
            <p className="appeal-text">{appealText}</p>
          )}
        </>
      ) : (
        ""
      )}

      {frequencySection}
      {amountSection}
      {errorMessage ? <div className="error">{errorMessage}</div> : ""}

      {mode === "preview" ? (
        <button>
          <RichText
            tagName="span"
            value={buttonText}
            allowedFormats={[]}
            onChange={(buttonText) => setAttributes({ buttonText })}
            onReplace={() => {}}
            onSplit={() => {}}
            placeholder={"Button text"}
          />
        </button>
      ) : (
        <button onClick={handleButtonClick}>{buttonText}</button>
      )}
      {footerText || isSelected ? (
        <>
          {mode === "preview" ? (
            <RichText
              tagName="p"
              value={footerText}
              allowedFormats={["core/bold", "core/link"]}
              onChange={(footerText) => setAttributes({ footerText })}
              placeholder={"Footer text"}
            />
          ) : (
            <p
              className="footer-text"
              dangerouslySetInnerHTML={{ __html: footerText }}
            />
          )}
        </>
      ) : (
        ""
      )}
    </>
  );
};
